import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Modal, Button } from "./ui";
import { Plus, Trash2, Save } from "lucide-react";

const field = "rounded-md border border-black/15 dark:border-white/15 bg-white/70 dark:bg-black/30 px-2 py-1 text-sm";
const blankTier = () => ({ up_to: "", mode: "flat", value: "" });

const DEFAULT_RULES = {
  cash: { enabled: false, same_atm: { mode: "flat", value: "" }, unrelated_atm: { mode: "flat", value: "" }, foreign_atm: { mode: "flat", value: "" } },
  package: { enabled: false, period: "monthly", amount: "", anniversary_date: "" },
  wires: { enabled: false, incoming: { tiers: [blankTier()] }, outgoing: { tiers: [blankTier()] } },
  transactional: { enabled: false, apply_to: "both", mode: "flat", flat_value: "", tiers: [blankTier()], base_fee: "", vat_pct: "", vat_mode: "pct", part3_pct: "", part3_mode: "pct", part3_label: "" },
};

const merge = (base, over) => {
  if (Array.isArray(base)) return over ?? base;
  if (typeof base === "object" && base) {
    const out = { ...base };
    for (const k of Object.keys(base)) out[k] = merge(base[k], (over || {})[k]);
    return out;
  }
  return over ?? base;
};

function ModeValue({ spec, onChange, testid }) {
  return (
    <span className="inline-flex items-center gap-1">
      <select value={spec.mode} onChange={(e) => onChange({ ...spec, mode: e.target.value })} className={field} data-testid={`${testid}-mode`}>
        <option value="flat">{TL("Flat")}</option>
        <option value="pct">{TL("% of amount")}</option>
      </select>
      <input type="number" step="any" placeholder={spec.mode === "pct" ? "%" : "amount"} value={spec.value}
        onChange={(e) => onChange({ ...spec, value: e.target.value })} className={`${field} w-24`} data-testid={`${testid}-value`} />
    </span>
  );
}

function Tiers({ tiers, onChange, testid }) {
  const set = (i, t) => onChange(tiers.map((x, k) => (k === i ? t : x)));
  return (
    <div className="space-y-1">
      {tiers.map((t, i) => (
        <div key={i} className="flex items-center gap-1 text-xs" data-testid={`${testid}-tier-${i}`}>
          <span className="opacity-60">{TL("up to")}</span>
          <input type="number" step="any" placeholder="∞" value={t.up_to} onChange={(e) => set(i, { ...t, up_to: e.target.value })} className={`${field} w-24`} data-testid={`${testid}-tier-${i}-upto`} />
          <ModeValue spec={t} onChange={(v) => set(i, { ...t, ...v })} testid={`${testid}-tier-${i}`} />
          {tiers.length > 1 && (
            <button onClick={() => onChange(tiers.filter((_, k) => k !== i))} className="text-[#DA1E28]" data-testid={`${testid}-tier-${i}-rm`}><Trash2 size={13} /></button>
          )}
        </div>
      ))}
      {tiers.length < 3 && (
        <button onClick={() => onChange([...tiers, blankTier()])} className="text-xs text-[var(--primary)] inline-flex items-center gap-1" data-testid={`${testid}-tier-add`}>
          <Plus size={12} /> Add tier
        </button>
      )}
    </div>
  );
}

function Section({ title, note, enabled, onToggle, children, testid }) {
  return (
    <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid={testid}>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={!!enabled} onChange={(e) => onToggle(e.target.checked)} data-testid={`${testid}-enabled`} />
        {title}
      </label>
      {note && <div className="text-xs opacity-60">{note}</div>}
      {enabled && <div className="space-y-2 ps-6">{children}</div>}
    </div>
  );
}

export default function LedgerRules({ app, accountId, open, onClose, onSaved }) {
  const [r, setR] = useState(DEFAULT_RULES);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMsg("");
    client.get(`/ledger/${app}/${accountId}/rules`).then(({ data }) => setR(merge(DEFAULT_RULES, data.rules || {})))
      .catch((e) => setMsg(apiErr(e)));
  }, [open, app, accountId]);

  const upd = (path, val) => setR((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    let o = next; const keys = path.split(".");
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
    o[keys[keys.length - 1]] = val;
    return next;
  });

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await client.put(`/ledger/${app}/${accountId}/rules`, { rules: r });
      onSaved && onSaved(data.ledger);
      onClose();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={TL("Bank charge rules")} testid="ledger-rules-modal" wide>
      <div className="space-y-3">
        <p className="text-xs opacity-60">{TL("Rules apply to this account only and auto-post a linked charge entry when triggered. Package fees post automatically on each anniversary.")}</p>

        <Section title={TL("C — Cash / ATM fees")} note="Charged when a new entry is tagged with an ATM trigger." enabled={r.cash.enabled} onToggle={(v) => upd("cash.enabled", v)} testid="rule-cash">
          <div className="flex items-center gap-2 text-xs"><span className="w-32 opacity-70">{TL("Same network")}</span><ModeValue spec={r.cash.same_atm} onChange={(v) => upd("cash.same_atm", v)} testid="rule-cash-same" /></div>
          <div className="flex items-center gap-2 text-xs"><span className="w-32 opacity-70">{TL("Unrelated ATM")}</span><ModeValue spec={r.cash.unrelated_atm} onChange={(v) => upd("cash.unrelated_atm", v)} testid="rule-cash-unrelated" /></div>
          <div className="flex items-center gap-2 text-xs"><span className="w-32 opacity-70">{TL("Foreign ATM")}</span><ModeValue spec={r.cash.foreign_atm} onChange={(v) => upd("cash.foreign_atm", v)} testid="rule-cash-foreign" /></div>
        </Section>

        <Section title={TL("P — Package fee (recurring)")} note="Auto-posts on the anniversary date each period." enabled={r.package.enabled} onToggle={(v) => upd("package.enabled", v)} testid="rule-package">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="opacity-70">{TL("Every")}</span>
            <select value={r.package.period} onChange={(e) => upd("package.period", e.target.value)} className={field} data-testid="rule-package-period">
              <option value="monthly">{TL("Monthly")}</option><option value="quarterly">{TL("Quarterly")}</option>
              <option value="semiannual">{TL("Semi-annual")}</option><option value="annual">{TL("Annual")}</option>
            </select>
            <span className="opacity-70">{TL("amount")}</span>
            <input type="number" step="any" value={r.package.amount} onChange={(e) => upd("package.amount", e.target.value)} className={`${field} w-28`} data-testid="rule-package-amount" />
            <span className="opacity-70">{TL("from")}</span>
            <input type="date" value={(r.package.anniversary_date || "").slice(0, 10)} onChange={(e) => upd("package.anniversary_date", e.target.value)} className={field} data-testid="rule-package-date" />
          </div>
        </Section>

        <Section title={TL("W — Wire fees")} note="Charged when an entry is tagged Incoming/Outgoing wire (up to 3 tiers by amount)." enabled={r.wires.enabled} onToggle={(v) => upd("wires.enabled", v)} testid="rule-wires">
          <div className="text-xs font-medium opacity-70">{TL("Incoming")}</div>
          <Tiers tiers={r.wires.incoming.tiers} onChange={(t) => upd("wires.incoming.tiers", t)} testid="rule-wire-in" />
          <div className="text-xs font-medium opacity-70 mt-2">{TL("Outgoing")}</div>
          <Tiers tiers={r.wires.outgoing.tiers} onChange={(t) => upd("wires.outgoing.tiers", t)} testid="rule-wire-out" />
        </Section>

        <Section title={TL("T — Transactional fee")} note="Charged on every credit/debit entry." enabled={r.transactional.enabled} onToggle={(v) => upd("transactional.enabled", v)} testid="rule-txn">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="opacity-70">{TL("Apply to")}</span>
            <select value={r.transactional.apply_to} onChange={(e) => upd("transactional.apply_to", e.target.value)} className={field} data-testid="rule-txn-applyto">
              <option value="both">{TL("Credits & debits")}</option><option value="credit">{TL("Credits only")}</option><option value="debit">{TL("Debits only")}</option>
            </select>
            <span className="opacity-70">{TL("Mode")}</span>
            <select value={r.transactional.mode} onChange={(e) => upd("transactional.mode", e.target.value)} className={field} data-testid="rule-txn-mode">
              <option value="flat">{TL("Flat")}</option><option value="variable">{TL("Variable (tiers)")}</option><option value="multipart">{TL("Multipart")}</option>
            </select>
          </div>
          {r.transactional.mode === "flat" && (
            <div className="flex items-center gap-2 text-xs"><span className="opacity-70">{TL("Flat fee")}</span>
              <input type="number" step="any" value={r.transactional.flat_value} onChange={(e) => upd("transactional.flat_value", e.target.value)} className={`${field} w-28`} data-testid="rule-txn-flat" /></div>
          )}
          {r.transactional.mode === "variable" && (
            <Tiers tiers={r.transactional.tiers} onChange={(t) => upd("transactional.tiers", t)} testid="rule-txn" />
          )}
          {r.transactional.mode === "multipart" && (
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2"><span className="w-28 opacity-70">{TL("Base fee (fixed)")}</span><input type="number" step="any" value={r.transactional.base_fee} onChange={(e) => upd("transactional.base_fee", e.target.value)} className={`${field} w-24`} data-testid="rule-txn-base" /></div>
              <div className="flex items-center gap-2"><span className="w-28 opacity-70">{TL("VAT (Part 2)")}</span>
                <select value={r.transactional.vat_mode} onChange={(e) => upd("transactional.vat_mode", e.target.value)} className={field} data-testid="rule-txn-vat-mode">
                  <option value="pct">{TL("% of base")}</option><option value="flat">{TL("Fixed amount")}</option>
                </select>
                <input type="number" step="any" placeholder={r.transactional.vat_mode === "flat" ? "amount" : "%"} value={r.transactional.vat_pct} onChange={(e) => upd("transactional.vat_pct", e.target.value)} className={`${field} w-24`} data-testid="rule-txn-vat" /></div>
              <div className="flex items-center gap-2"><span className="w-28 opacity-70">{TL("Part 3")}</span>
                <select value={r.transactional.part3_mode} onChange={(e) => upd("transactional.part3_mode", e.target.value)} className={field} data-testid="rule-txn-part3-mode">
                  <option value="pct">{TL("% of amount")}</option><option value="flat">{TL("Fixed amount")}</option>
                </select>
                <input type="number" step="any" placeholder={r.transactional.part3_mode === "flat" ? "amount" : "%"} value={r.transactional.part3_pct} onChange={(e) => upd("transactional.part3_pct", e.target.value)} className={`${field} w-24`} data-testid="rule-txn-part3" />
                <input placeholder={TL("Part 3 label")} value={r.transactional.part3_label} onChange={(e) => upd("transactional.part3_label", e.target.value)} className={`${field} w-32`} data-testid="rule-txn-part3-label" /></div>
            </div>
          )}
        </Section>

        {msg && <div className="text-xs text-[#DA1E28]" data-testid="ledger-rules-msg">{msg}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="ledger-rules-cancel">{TL("Cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="ledger-rules-save"><Save size={14} /> {TL("Save rules")}</Button>
        </div>
      </div>
    </Modal>
  );
}
